<script setup lang="ts">
import { AppSidePanelHeader } from '@app/components/parts/appSidePanel';
import type { TAppSidePanelHeaderAction } from '@app/components/parts/appSidePanel';
import Badge from '@ui/components/badge.vue';
import Icon from '@ui/components/icon.vue';
import { translate } from '@utils/I18n';
import { computed } from 'vue';
import { useHelp } from './useHelp';

const {
    isArticleSection,
    isSupportSection,
    helpData,
    externalArticleUrl,
    currentContentType,
    goBackToMenu,
    closeHelpPanel,
} = useHelp();

const texts = computed(() => helpData.value.texts.common);
const showNewsBadge = computed(() => currentContentType.value === 'news');
const newsBadgeText = computed(() => texts.value.news_badge ?? 'News');

const labelBack = translate('common.back');
const labelOpenNewWindow = translate('common.open_new_window');

/** The external article is a plain link: the browser opens it, so the header action needs no handler. */
const headerActions = computed<TAppSidePanelHeaderAction[]>(() => {
    if (!externalArticleUrl.value) {
        return [];
    }

    return [
        {
            name: 'open-external-article',
            icon: 'far fa-external-link',
            href: externalArticleUrl.value,
            target: '_blank',
            tooltip: labelOpenNewWindow.value,
        },
    ];
});
</script>

<template>
    <AppSidePanelHeader
        :title="texts.headerTitle ?? ''"
        :actions="headerActions"
        @close="closeHelpPanel"
    >
        <template #leading>
            <button
                v-if="isArticleSection || isSupportSection"
                type="button"
                class="text-brand-500 hover:text-brand-600 inline-flex cursor-pointer p-0 transition focus:outline-none focus:ring-0"
                :aria-label="labelBack"
                @click="goBackToMenu"
            >
                <Icon
                    icon="far fa-chevron-left"
                    class="shrink-0"
                ></Icon>
            </button>
            <Icon
                v-else
                icon="far fa-question-circle"
                class="shrink-0"
            ></Icon>
        </template>
        <template #afterTitle>
            <Badge
                v-if="showNewsBadge"
                :text="newsBadgeText"
                size="medium"
                theme="light-yellow"
                outlined
                class="-my-1 shrink-0"
            ></Badge>
        </template>
    </AppSidePanelHeader>
</template>
